// ─── src/components/dashboard/FnfSheetTable.tsx ──────────────────────────────
//
// A Google-Sheets-style grid for FNF survey responses:
//
//   * Every one of the ~45 survey fields lives in its own column, laid out
//     left-to-right in section order — no more per-question modal to see
//     what someone answered.
//   * The first column is frozen (sticky) so the respondent's identifier
//     (governorate + submitted date) stays in view while scrolling right.
//   * The header row is sticky vertically, so column titles stay visible
//     as you scroll through many rows.
//   * Clicking a header sorts by that column (asc → desc → off).
//   * Long cell values are truncated to a fixed width and the full string
//     is available via the browser tooltip on hover.
//   * A row click opens the existing detail modal — the sheet is scannable,
//     the modal is exhaustive.

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import {
  ChevronDown, ChevronUp, ChevronsUpDown,
  Search, Settings2, Trash2, X,
} from "lucide-react";
import type { FnfSurveyResponse } from "../../api/fnfSurvey";

// LocalStorage key for the column-visibility preferences. Persisting these
// per-browser means ops's chosen "quick view" survives a page reload.
const VISIBILITY_STORAGE_KEY = "fnf-sheet-hidden-cols";

// ── Column spec ─────────────────────────────────────────────────────────────

type CellValue = string | number | null | undefined;

interface ColumnDef {
  /** Object key on the row, used for both sorting and rendering. */
  key: keyof FnfSurveyResponse | "actions";
  /** Header label shown on the sheet. */
  label: string;
  /** Fixed cell width in px — matches header for column alignment. */
  width: number;
  /** How to project a row to a comparable/renderable value. */
  get?: (r: FnfSurveyResponse) => CellValue;
  /** Custom cell renderer (overrides the default text render). */
  render?: (r: FnfSurveyResponse) => React.ReactNode;
  /** Section label used to render the group ribbon under the header. */
  section?: string;
  /** Skip sort UI entirely (e.g. free-text descriptions). */
  noSort?: boolean;
}

// Helpers to render array / grid columns in a compact single-cell form.
const arr = (v: string[] | undefined | null) => (v && v.length ? v.join(" · ") : "—");
const bool = (v: boolean | undefined | null) => (v == null ? "—" : v ? "Yes" : "No");
const grid = (v: Record<string, string> | undefined | null) =>
  v ? Object.entries(v).map(([k, val]) => `${k}: ${val}`).join(" · ") : "—";
const date = (v: string | undefined | null) =>
  v ? new Date(v).toLocaleDateString() : "—";

// Order tracks the survey's own section order — respondents' answers show
// up left-to-right the same way they filled them in.
const COLUMNS: ColumnDef[] = [
  // Sticky identifier (rendered separately so it can be pinned).
  {
    key: "createdAt", label: "Submitted", section: "Meta", width: 120,
    get: (r) => r.createdAt, render: (r) => date(r.createdAt),
  },

  // Section 1
  { key: "projectRole",    label: "Role",           section: "S1", width: 220, get: (r) => r.projectRole },
  { key: "syriaRelation",  label: "Syria relation", section: "S1", width: 300, get: (r) => r.syriaRelation },

  // Section 2
  {
    key: "residesInSyria", label: "In Syria?", section: "S2", width: 90,
    get: (r) => (r.residesInSyria ? 1 : 0), render: (r) => bool(r.residesInSyria),
  },
  { key: "residenceCountry", label: "Residence country", section: "S2", width: 140, get: (r) => r.residenceCountry ?? "" },
  { key: "age",              label: "Age",               section: "S2", width: 70,  get: (r) => r.age },
  { key: "gender",           label: "Gender",            section: "S2", width: 110, get: (r) => r.gender },
  {
    key: "isWorkingOnProject", label: "Active?", section: "S2", width: 90,
    get: (r) => (r.isWorkingOnProject ? 1 : 0), render: (r) => bool(r.isWorkingOnProject),
  },
  { key: "projectDuration", label: "Duration",       section: "S2", width: 170, get: (r) => r.projectDuration },
  { key: "projectStage",    label: "Stage",          section: "S2", width: 260, get: (r) => r.projectStage },
  { key: "projectType",     label: "Type",           section: "S2", width: 220, get: (r) => r.projectType },
  { key: "projectSector",   label: "Sector",         section: "S2", width: 140, get: (r) => r.projectSector },
  {
    key: "projectDescription", label: "Description", section: "S2", width: 260,
    get: (r) => r.projectDescription, noSort: true,
  },
  { key: "womenOwnership", label: "Women ownership", section: "S2", width: 160, get: (r) => r.womenOwnership },
  { key: "legalStatus",    label: "Legal status",    section: "S2", width: 220, get: (r) => r.legalStatus },
  { key: "registrationMonthYear", label: "Reg. MM/YYYY", section: "S2", width: 110, get: (r) => r.registrationMonthYear ?? "" },
  { key: "salesChange",    label: "Sales vs LY",     section: "S2", width: 160, get: (r) => r.salesChange },

  // Section 3
  {
    key: "receivedFunding", label: "Funded?", section: "S3", width: 90,
    get: (r) => (r.receivedFunding ? 1 : 0), render: (r) => bool(r.receivedFunding),
  },
  { key: "fundingTypes",     label: "Funding types",     section: "S3", width: 220, render: (r) => arr(r.fundingTypes),     noSort: true },
  { key: "fundingSources",   label: "Funding sources",   section: "S3", width: 220, render: (r) => arr(r.fundingSources),   noSort: true },
  { key: "fundingObstacles", label: "Funding obstacles", section: "S3", width: 220, render: (r) => arr(r.fundingObstacles), noSort: true },

  // Section 4
  { key: "startupDifficulty", label: "Difficulty",   section: "S4", width: 130, get: (r) => r.startupDifficulty },
  { key: "challengeGrid",     label: "Challenge grid", section: "S4", width: 300, render: (r) => grid(r.challengeGrid), noSort: true },
  { key: "topChallenges",     label: "Top challenges", section: "S4", width: 220, render: (r) => arr(r.topChallenges),  noSort: true },
  { key: "neededServices",    label: "Needed services", section: "S4", width: 220, render: (r) => arr(r.neededServices), noSort: true },

  // Section 5
  { key: "targetCustomers",       label: "Target customers",       section: "S5", width: 200, render: (r) => arr(r.targetCustomers),       noSort: true },
  { key: "customerLocations",     label: "Customer locations",     section: "S5", width: 200, render: (r) => arr(r.customerLocations),     noSort: true },
  { key: "acquisitionChannels",   label: "Acquisition channels",   section: "S5", width: 220, render: (r) => arr(r.acquisitionChannels),   noSort: true },
  { key: "acquisitionObstacles",  label: "Acquisition obstacles",  section: "S5", width: 220, render: (r) => arr(r.acquisitionObstacles),  noSort: true },

  // Section 6
  { key: "recoveryImportance",    label: "Recovery (1–5)",   section: "S6", width: 100, get: (r) => r.recoveryImportance },
  { key: "recoveryDomains",       label: "Recovery domains", section: "S6", width: 220, render: (r) => arr(r.recoveryDomains),   noSort: true },
  { key: "recoveryActivityStatus", label: "Activity status", section: "S6", width: 200, get: (r) => r.recoveryActivityStatus },
  { key: "mainContribution",      label: "Main contribution", section: "S6", width: 220, render: (r) => arr(r.mainContribution), noSort: true },

  // Section 7
  { key: "businessExperience",     label: "Biz exp. (0–5)",     section: "S7", width: 100, get: (r) => r.businessExperience },
  { key: "hardestSkills",          label: "Hardest skills",     section: "S7", width: 220, render: (r) => arr(r.hardestSkills), noSort: true },
  { key: "digitalTools",           label: "Digital tools",      section: "S7", width: 220, render: (r) => arr(r.digitalTools),  noSort: true },
  { key: "conditionsCurrentGrid",  label: "Conditions (now)",   section: "S7", width: 260, render: (r) => grid(r.conditionsCurrentGrid), noSort: true },
  { key: "conditionsYoyGrid",      label: "Conditions (vs LY)", section: "S7", width: 260, render: (r) => grid(r.conditionsYoyGrid),     noSort: true },

  // Section 8
  { key: "supportReceived", label: "Support received", section: "S8", width: 220, render: (r) => arr(r.supportReceived), noSort: true },
  { key: "programResults",  label: "Program results",  section: "S8", width: 220, render: (r) => arr(r.programResults),  noSort: true },
  { key: "neededSupport",   label: "Needed support",   section: "S8", width: 220, render: (r) => arr(r.neededSupport),   noSort: true },

  // Section 9
  { key: "awareGovProgram",   label: "Aware gov?",       section: "S9", width: 240, get: (r) => r.awareGovProgram },
  { key: "neededGovServices", label: "Needed gov services", section: "S9", width: 220, render: (r) => arr(r.neededGovServices), noSort: true },
  { key: "topReforms",        label: "Top reforms",      section: "S9", width: 260, render: (r) => arr(r.topReforms),           noSort: true },
  { key: "openComment",       label: "Open comment",     section: "S9", width: 220, get: (r) => r.openComment ?? "", noSort: true },

  // Section 10
  {
    key: "consentInterview", label: "Interview?", section: "S10", width: 90,
    get: (r) => (r.consentInterview ? 1 : 0), render: (r) => bool(r.consentInterview),
  },
  {
    key: "consentCaseStudy", label: "Case study?", section: "S10", width: 90,
    get: (r) => (r.consentCaseStudy ? 1 : 0), render: (r) => bool(r.consentCaseStudy),
  },
  { key: "contactInfo", label: "Contact", section: "S10", width: 200, get: (r) => r.contactInfo ?? "" },

  // Actions column — rendered last and not scrollable off (sticky right).
  { key: "actions", label: "", section: "", width: 60, noSort: true },
];

// The one column that sticks to the leading edge — "Governorate" makes the
// most sense as an identifier when scanning rows.
const FROZEN_COL: ColumnDef = {
  key: "projectGovernorate",
  label: "Governorate",
  section: "S2",
  width: 150,
  get: (r) => r.projectGovernorate,
};

// ── Component ────────────────────────────────────────────────────────────────

type SortKey = ColumnDef["key"];
type SortDir = "asc" | "desc";

interface Props {
  rows: FnfSurveyResponse[];
  onOpenDetail: (r: FnfSurveyResponse) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

export function FnfSheetTable({ rows, onOpenDetail, onDelete, isDeleting }: Props) {
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>({
    key: "createdAt",
    dir: "desc",
  });

  // ── Column visibility (persisted per-browser) ───────────────
  // Stored as an array of hidden keys so a new column added later is
  // visible by default rather than accidentally hidden.
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem(VISIBILITY_STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return new Set(parsed as string[]);
      return new Set();
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        VISIBILITY_STORAGE_KEY,
        JSON.stringify(Array.from(hiddenCols)),
      );
    } catch {
      // Private mode or blocked storage — preferences just don't persist.
    }
  }, [hiddenCols]);

  const toggleColumn = (key: string) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const showAll = () => setHiddenCols(new Set());
  const hideAllHideable = () =>
    setHiddenCols(new Set(COLUMNS.filter((c) => c.key !== "actions").map((c) => String(c.key))));

  // Toggle column-picker popover with click-outside close.
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!colPickerOpen) return;
    const onDown = (e: MouseEvent) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target as Node)) {
        setColPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [colPickerOpen]);

  const visibleColumns = useMemo(
    () => COLUMNS.filter((c) => !hiddenCols.has(String(c.key))),
    [hiddenCols],
  );

  // ── Local text filter across visible cells ─────────────────
  const [localFilter, setLocalFilter] = useState("");
  const normalizedFilter = localFilter.trim().toLowerCase();

  // ── Sort + filter pipeline ─────────────────────────────────
  const displayed = useMemo(() => {
    // Sort first (stable across visible/hidden columns).
    let out = rows;
    if (sort) {
      const col = [FROZEN_COL, ...COLUMNS].find((c) => c.key === sort.key);
      if (col?.get) {
        const dir = sort.dir === "asc" ? 1 : -1;
        out = [...rows].sort((a, b) => {
          const av = col.get?.(a);
          const bv = col.get?.(b);
          // Push nullish to the end regardless of direction — cleaner sheet
          // reading experience.
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          if (typeof av === "number" && typeof bv === "number") {
            return (av - bv) * dir;
          }
          return String(av).localeCompare(String(bv)) * dir;
        });
      }
    }

    // Then filter — only match against the columns actually visible on
    // screen, so hiding a noisy column also drops it from the search
    // haystack. Frozen governorate column is always included.
    if (!normalizedFilter) return out;
    const cols = [FROZEN_COL, ...visibleColumns].filter((c) => c.key !== "actions");
    return out.filter((r) =>
      cols.some((col) => {
        const raw = col.render ? undefined : col.get?.(r);
        // For custom-rendered cells, fall back to `get` if present, else
        // stringify the row's underlying key when it's a plain scalar.
        const candidate =
          raw != null
            ? String(raw)
            : col.get
              ? String(col.get(r) ?? "")
              : "";
        // For array/grid columns we lack a plain scalar — reach into the
        // row to build a searchable string.
        if (!candidate) {
          const v = (r as unknown as Record<string, unknown>)[String(col.key)];
          if (Array.isArray(v)) return v.join(" ").toLowerCase().includes(normalizedFilter);
          if (v && typeof v === "object") {
            return Object.values(v as Record<string, string>).join(" ").toLowerCase().includes(normalizedFilter);
          }
          return String(v ?? "").toLowerCase().includes(normalizedFilter);
        }
        return candidate.toLowerCase().includes(normalizedFilter);
      }),
    );
  }, [rows, sort, normalizedFilter, visibleColumns]);

  // Older code referred to `sortedRows` — kept for readability of the
  // render section.
  const sortedRows = displayed;

  const clickHeader = (key: SortKey, noSort?: boolean) => {
    if (noSort) return;
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return null;
    });
  };

  const sortIcon = (key: SortKey, noSort?: boolean) => {
    if (noSort) return null;
    const active = sort?.key === key;
    if (!active) return <ChevronsUpDown size={12} className="opacity-40" />;
    return sort.dir === "asc"
      ? <ChevronUp size={12} />
      : <ChevronDown size={12} />;
  };

  const cellStyle = (width: number): CSSProperties => ({ width, minWidth: width, maxWidth: width });

  // Sticky-first-column styling — a solid bg so header/first-cell layer over
  // any scrolled-under content, and a right border to divide.
  const frozenStickyStyle: CSSProperties = {
    position: "sticky",
    insetInlineStart: 0,
    zIndex: 2,
  };
  // Sticky-right actions column so Delete is always accessible.
  const actionsStickyStyle: CSSProperties = {
    position: "sticky",
    insetInlineEnd: 0,
    zIndex: 2,
  };

  const hiddenCount = COLUMNS.filter(
    (c) => c.key !== "actions" && hiddenCols.has(String(c.key)),
  ).length;
  const totalHideable = COLUMNS.filter((c) => c.key !== "actions").length;

  return (
    <div className="card overflow-hidden">
      {/* Toolbar — local filter + column visibility ─────────────── */}
      <div className="px-4 py-3 border-b border-brand-border flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
            <input
              type="text"
              value={localFilter}
              onChange={(e) => setLocalFilter(e.target.value)}
              placeholder="Filter visible sheet…"
              className="w-full pl-9 pr-8 py-2 text-xs bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
            />
            {localFilter && (
              <button
                type="button"
                onClick={() => setLocalFilter("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white"
                title="Clear filter"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Column-picker gear — click opens a checkbox popover. */}
          <div className="relative" ref={colPickerRef}>
            <button
              type="button"
              onClick={() => setColPickerOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-brand-border text-brand-muted hover:text-white hover:border-brand-orange text-xs font-semibold"
              title="Show / hide columns"
            >
              <Settings2 size={13} />
              Columns
              {hiddenCount > 0 && (
                <span className="text-brand-orange">· {totalHideable - hiddenCount}/{totalHideable}</span>
              )}
            </button>
            {colPickerOpen && (
              <div className="absolute end-0 mt-2 w-72 max-h-[60vh] overflow-y-auto rounded-xl border border-brand-border bg-brand-card shadow-xl z-[50] p-3">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-brand-muted uppercase tracking-wide">Columns</span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button" onClick={showAll}
                      className="text-brand-green hover:brightness-125"
                    >
                      Show all
                    </button>
                    <button
                      type="button" onClick={hideAllHideable}
                      className="text-brand-orange hover:brightness-125"
                    >
                      Hide all
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-0.5">
                  {COLUMNS.filter((c) => c.key !== "actions").map((c) => {
                    const key = String(c.key);
                    const on = !hiddenCols.has(key);
                    return (
                      <label
                        key={key}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-xs text-white"
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggleColumn(key)}
                          className="accent-brand-green"
                        />
                        <span className="flex-1 truncate">{c.label}</span>
                        {c.section && (
                          <span className="text-[10px] text-brand-muted uppercase">{c.section}</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-brand-muted whitespace-nowrap">
          <span>
            <span className="text-white font-semibold">{sortedRows.length}</span>
            {normalizedFilter && (
              <> of <span className="text-white font-semibold">{rows.length}</span></>
            )} row{sortedRows.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      <div className="px-4 py-2 border-b border-brand-border text-[10px] uppercase tracking-wide text-brand-muted">
        Scroll horizontally · click column header to sort · click a row to open
      </div>

      <div className="overflow-auto max-h-[70vh]">
        <table className="text-xs" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead className="sticky top-0 z-[3]" style={{ background: "#0f172a" }}>
            <tr>
              {/* Frozen first column header */}
              <th
                className="px-3 py-2 text-left uppercase tracking-wide text-brand-muted border-b border-r border-brand-border"
                style={{
                  ...cellStyle(FROZEN_COL.width),
                  ...frozenStickyStyle,
                  background: "#0f172a",
                  cursor: "pointer",
                }}
                onClick={() => clickHeader(FROZEN_COL.key)}
              >
                <div className="flex items-center gap-1">
                  <span>{FROZEN_COL.label}</span>
                  {sortIcon(FROZEN_COL.key)}
                </div>
              </th>

              {visibleColumns.map((col) => {
                const isActions = col.key === "actions";
                const stickyStyle = isActions ? actionsStickyStyle : undefined;
                return (
                  <th
                    key={String(col.key)}
                    className="px-3 py-2 text-left uppercase tracking-wide text-brand-muted border-b border-brand-border"
                    style={{
                      ...cellStyle(col.width),
                      ...(stickyStyle ?? {}),
                      background: "#0f172a",
                      cursor: col.noSort ? "default" : "pointer",
                      borderInlineStart: isActions ? "1px solid rgba(255,255,255,0.08)" : undefined,
                    }}
                    onClick={() => clickHeader(col.key, col.noSort)}
                    title={col.section ? `Section ${col.section}` : undefined}
                  >
                    <div className="flex items-center gap-1">
                      <span>{col.label}</span>
                      {sortIcon(col.key, col.noSort)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((r, i) => {
              const rowBg = i % 2 === 0 ? "#111827" : "#0f172a";
              return (
                <tr
                  key={r.id}
                  className="hover:brightness-125 cursor-pointer"
                  onClick={() => onOpenDetail(r)}
                >
                  {/* Frozen first cell */}
                  <td
                    className="px-3 py-2 text-white border-b border-r border-brand-border truncate"
                    style={{
                      ...cellStyle(FROZEN_COL.width),
                      ...frozenStickyStyle,
                      background: rowBg,
                    }}
                    title={FROZEN_COL.get?.(r) ? String(FROZEN_COL.get(r)) : ""}
                  >
                    {FROZEN_COL.get?.(r) ?? "—"}
                  </td>

                  {visibleColumns.map((col) => {
                    if (col.key === "actions") {
                      return (
                        <td
                          key={String(col.key)}
                          className="px-2 py-1 border-b border-brand-border text-right"
                          style={{
                            ...cellStyle(col.width),
                            ...actionsStickyStyle,
                            background: rowBg,
                            borderInlineStart: "1px solid rgba(255,255,255,0.08)",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm("Delete this response?")) onDelete(r.id);
                            }}
                            disabled={isDeleting}
                            className="p-2 rounded-lg hover:bg-brand-orange/20 text-brand-orange"
                            title="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      );
                    }

                    const text = col.render
                      ? col.render(r)
                      : (() => {
                          const v = col.get?.(r);
                          return v === "" || v == null ? "—" : String(v);
                        })();

                    return (
                      <td
                        key={String(col.key)}
                        className="px-3 py-2 text-brand-muted border-b border-brand-border truncate"
                        style={{ ...cellStyle(col.width), background: rowBg }}
                        title={typeof text === "string" ? text : undefined}
                      >
                        <span className="text-white">{text}</span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
